import {createAsyncThunk} from "@reduxjs/toolkit";
import {isUndefined, omitBy, pick} from "lodash";
import type {CarAuction, CarItem} from "@/types";
import type {RootState} from "@/store";
import {supabase} from "@/utils/supabase.ts";
import {
    deleteFromR2,
    getR2ObjectKey,
    keysToCamelCase,
    keysToSnakeCase,
    makeFileName,
    normalizeError,
    prepareImageFile,
    uploadToR2
} from "@/utils";

const VEHICLE_MEDIA_BUCKET = "vehicle-media";

const editableVehicleTableColumns = [
    "name",
    "year",
    "brand",
    "model",
    "mileage",
    "transmission",
    "fuel_type",
    "engine",
    "capacity",
    "drivetrain",
    "body",
    "vin",
    "title_status",
    "color",
    "interior",
    "images",
    "video",
    "tags",
    "description",
    "condition",
    "owners",
    "interior_color",
    "doors",
    "seats",
    "horsepower",
    "torque",
    "imported",
    "warranty",
    "verified",
    "featured",
    "active",
    "published"
] as const;

const cleanForDB = (data: unknown) =>
    omitBy(pick(data as Record<string, unknown>, editableVehicleTableColumns), isUndefined);

async function uploadEditedVehicleImage(source: string, path: string, index: number) {
    if (!source.startsWith("blob:")) {
        return source;
    }

    const filename = makeFileName(`vehicle_${index}`, `vehicle_${index}.webp`);
    const key = `${path}/${filename}`;
    const file = await prepareImageFile(source, filename, {
        maxSizeMB: 1,
        maxWidthOrHeight: 1280
    });

    return uploadToR2(VEHICLE_MEDIA_BUCKET, key, file, "image/webp");
}

async function resolveEditedImages(images: string[], sellerId: string, vehicleId: string) {
    const path = `${sellerId}/${vehicleId}`;
    return Promise.all(images.map((image, index) => uploadEditedVehicleImage(image, path, index)));
}

async function removeDeletedImages(previousImages: string[], nextImages: string[]) {
    const removedImages = previousImages.filter((image) => !nextImages.includes(image));

    const results = await Promise.allSettled(
        removedImages.map(async (image) => {
            const key = getR2ObjectKey(VEHICLE_MEDIA_BUCKET, image);
            if (!key) {
                return;
            }

            await deleteFromR2(VEHICLE_MEDIA_BUCKET, key);
        })
    );

    results.forEach((result, index) => {
        if (result.status === "rejected") {
            console.warn("[editVehicleThunk] failed to delete removed image", {
                image: removedImages[index],
                error: result.reason
            });
        }
    });
}

export const editVehicleThunk = createAsyncThunk<
    CarAuction | CarItem,
    CarAuction | CarItem,
    { state: RootState; rejectValue: string }
>(
    "vehicle/editVehicle",
    async (draftVehicle, {getState, rejectWithValue}) => {
        try {
            const {authentication, vehicle} = getState();
            const stateUserId = authentication.dealerUser?.id;
            const sellerId = authentication.user?.id;

            const {data: authData, error: authError} = await supabase.auth.getUser();
            if (authError) {
                return rejectWithValue(authError.message);
            }

            const authUserId = authData.user?.id;
            if (!authUserId) {
                return rejectWithValue("Session expired. Please log in again.");
            }

            if (stateUserId && stateUserId !== authUserId) {
                return rejectWithValue("Auth mismatch detected.");
            }
            if (!sellerId) {
                return rejectWithValue("No dealership profile found.");
            }

            const existingVehicle = vehicle.currentVehicle;
            if (!existingVehicle || existingVehicle.id !== draftVehicle.id) {
                return rejectWithValue("Current vehicle is not loaded.");
            }

            if (existingVehicle.type !== draftVehicle.type) {
                return rejectWithValue("Changing vehicle type is not supported here.");
            }

            const resolvedImages = await resolveEditedImages(draftVehicle.images, sellerId, draftVehicle.id);
            await removeDeletedImages(existingVehicle.images, resolvedImages);

            const vehicleUpdate = cleanForDB(keysToSnakeCase({
                ...draftVehicle,
                images: resolvedImages
            }));

            const {error: vehicleError} = await supabase
                .from("vehicles")
                .update(vehicleUpdate)
                .eq("id", draftVehicle.id)
                .eq("seller_id", sellerId);

            if (vehicleError) {
                return rejectWithValue(vehicleError.message);
            }

            if (draftVehicle.type === "listing") {
                const {error: listingError} = await supabase
                    .from("listings")
                    .update({
                        price: draftVehicle.price,
                        negotiable: draftVehicle.negotiable ?? false,
                    })
                    .eq("vehicle_id", draftVehicle.id);

                if (listingError) {
                    return rejectWithValue(listingError.message);
                }
            }

            if (draftVehicle.type === "auction") {
                const hasBids = (existingVehicle.type === "auction" ? existingVehicle.bids?.length : 0) ?? 0;
                const auctionUpdate: Record<string, unknown> = {
                    starting_bid: draftVehicle.startingBid,
                    reserve_price: draftVehicle.reservePrice ?? null,
                    ending: new Date(draftVehicle.ending).toISOString()
                };

                if (hasBids === 0) {
                    auctionUpdate.current_bid = draftVehicle.startingBid;
                }

                const {error: auctionError} = await supabase
                    .from("auctions")
                    .update(auctionUpdate)
                    .eq("vehicle_id", draftVehicle.id)
                    .eq("seller_id", sellerId);

                if (auctionError) {
                    return rejectWithValue(auctionError.message);
                }
            }

            const {data, error} = await supabase
                .from("vehicles")
                .select("*, auction:auctions(*), listing:listings(*)")
                .eq("id", draftVehicle.id)
                .eq("seller_id", sellerId)
                .single();

            if (error) {
                return rejectWithValue(error.message);
            }

            if (!data) {
                return rejectWithValue("Updated vehicle not found");
            }

            if (data.type === "auction") {
                return keysToCamelCase<CarAuction>({
                    ...data.auction,
                    ...data
                });
            }

            if (data.type === "listing") {
                return keysToCamelCase<CarItem>({
                    ...data.listing,
                    ...data
                });
            }

            return rejectWithValue("Invalid vehicle type");
        } catch (error) {
            return rejectWithValue(normalizeError(error, "Failed to update vehicle"));
        }
    }
);
